import { useEffect, useState } from "react";
import { Trans, useLingui } from "@lingui/macro";
import { CheckCircle2, Info, Loader2, Search } from "lucide-react";
import { Button } from "@app/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import { Checkbox } from "@app/components/ui/checkbox";
import { Input } from "@app/components/ui/input";
import { Label } from "@app/components/ui/label";
import {
  isOrganisationDetail,
  type Organisation,
  type OrganisationDetail,
  type OrganisationRole,
} from "@app/components/organisation/types";
import { useOrganisationMessages } from "@app/components/organisation/useOrganisationMessages";
import API from "@app/services/api";
import useUser from "@app/zustand/user";

interface CompanyLookup {
  raison_sociale: string | null;
  adresse: string | null;
  code_postal: string | null;
  ville: string | null;
  ferme: boolean;
}

interface IdentityForm {
  personnel: boolean;
  siret: string;
  raison_sociale: string;
  numero_tva: string;
  // A person billed personally: the invoicing service files them under two names.
  prenom: string;
  nom: string;
  adresse: string;
  complement_adresse: string;
  code_postal: string;
  ville: string;
  email_facturation: string;
}

const EMPTY_FORM: IdentityForm = {
  personnel: false,
  siret: "",
  raison_sociale: "",
  numero_tva: "",
  prenom: "",
  nom: "",
  adresse: "",
  complement_adresse: "",
  code_postal: "",
  ville: "",
  email_facturation: "",
};

function isLookup(res: unknown): res is CompanyLookup {
  return (
    typeof res === "object" &&
    res !== null &&
    typeof (res as CompanyLookup).ferme === "boolean"
  );
}

function digitsOnly(value: string): string {
  return value.replace(/[\s.-]/g, "");
}

/** The form for an organisation as stored, or a blank one addressed to the session's e-mail. */
function formFrom(organisation: Organisation | null): IdentityForm {
  if (!organisation) {
    const email =
      (useUser.getState().user as { email?: string } | null)?.email ?? "";
    return { ...EMPTY_FORM, email_facturation: email };
  }
  return {
    personnel: organisation.siret === null,
    siret: organisation.siret ?? "",
    raison_sociale: organisation.raison_sociale,
    numero_tva: organisation.numero_tva ?? "",
    prenom: organisation.prenom ?? "",
    nom: organisation.nom ?? "",
    adresse: organisation.adresse,
    complement_adresse: organisation.complement_adresse ?? "",
    code_postal: organisation.code_postal,
    ville: organisation.ville,
    email_facturation: organisation.email_facturation,
  };
}

interface OrganisationIdentityProps {
  /** ``null`` to create one: the caller becomes its gestionnaire. */
  organisation: Organisation | null;
  role: OrganisationRole | null;
  onSaved: (detail: OrganisationDetail) => void;
  onCancel?: () => void;
}

/**
 * The organisation's identity — what its invoices are addressed to (PT-117, PT-119).
 *
 * Its gestionnaire edits it; a membre reads it. Prefilled from the SIRET through the public company
 * registry, typed by hand when the registry does not answer.
 */
export default function OrganisationIdentity({
  organisation,
  role,
  onSaved,
  onCancel,
}: OrganisationIdentityProps) {
  const { t } = useLingui();
  const { messages, failureMessage } = useOrganisationMessages();
  const [form, setForm] = useState<IdentityForm>(() => formFrom(organisation));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [searching, setSearching] = useState(false);
  const [closedNotice, setClosedNotice] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setForm(formFrom(organisation));
    setSaved(false);
    setError("");
  }, [organisation]);

  function update(field: keyof IdentityForm, value: string | boolean) {
    setForm((current) => ({ ...current, [field]: value }));
    setSaved(false);
  }

  function validate(): string | null {
    if (!form.personnel && !/^\d{14}$/.test(digitsOnly(form.siret))) {
      return messages.siret_invalide;
    }
    const named = form.personnel
      ? form.prenom.trim() && form.nom.trim()
      : form.raison_sociale.trim();
    if (!named || !form.adresse.trim() || !form.ville.trim()) {
      return t`Renseignez tous les champs obligatoires.`;
    }
    if (!/^\d{5}$/.test(form.code_postal.trim())) {
      return messages.code_postal_invalide;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email_facturation.trim())) {
      return t`L'adresse e-mail de facturation n'est pas valide.`;
    }
    return null;
  }

  function lookup() {
    const siret = digitsOnly(form.siret);
    if (!/^\d{14}$/.test(siret)) {
      setError(messages.siret_invalide);
      return;
    }
    setSearching(true);
    setError("");
    setClosedNotice(false);
    API.get({ path: `/billing/siret/${siret}` })
      .then((res: unknown) => {
        if (!isLookup(res)) {
          setError(failureMessage(res, messages.registre_indisponible));
          return;
        }
        setForm((current) => ({
          ...current,
          siret,
          raison_sociale: res.raison_sociale ?? current.raison_sociale,
          adresse: res.adresse ?? current.adresse,
          code_postal: res.code_postal ?? current.code_postal,
          ville: res.ville ?? current.ville,
        }));
        setClosedNotice(res.ferme);
        setSaved(false);
      })
      .finally(() => setSearching(false));
  }

  function save() {
    const invalid = validate();
    if (invalid) {
      setError(invalid);
      return;
    }
    setSaving(true);
    setError("");
    const body = {
      // A person's name is derived from the two they type.
      raison_sociale: form.personnel ? null : form.raison_sociale,
      siret: form.personnel ? null : digitsOnly(form.siret),
      numero_tva: form.personnel ? null : form.numero_tva || null,
      prenom: form.personnel ? form.prenom : null,
      nom: form.personnel ? form.nom : null,
      adresse: form.adresse,
      complement_adresse: form.complement_adresse || null,
      code_postal: form.code_postal,
      ville: form.ville,
      email_facturation: form.email_facturation,
    };
    const request = organisation
      ? API.put({ path: `/organisation/${organisation.id}`, body })
      : API.post({ path: "/organisation", body });
    request
      .then((res: unknown) => {
        if (isOrganisationDetail(res)) {
          setSaved(true);
          onSaved(res);
        } else {
          setError(
            failureMessage(res, t`L'enregistrement a échoué. Réessayez.`),
          );
        }
      })
      .finally(() => setSaving(false));
  }

  if (organisation && role === "membre") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>
            <Trans>Identité et facturation</Trans>
          </CardTitle>
          <CardDescription>
            <Trans>
              Les factures de l'organisation sont adressées à cette entité. Seul
              un gestionnaire peut la modifier.
            </Trans>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-slate-500">
                <Trans>Facturé à</Trans>
              </dt>
              <dd className="font-medium text-slate-900">
                {organisation.raison_sociale}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">
                <Trans>SIRET</Trans>
              </dt>
              <dd className="text-slate-900">
                {organisation.siret ?? <Trans>Facturé à titre personnel</Trans>}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">
                <Trans>Adresse</Trans>
              </dt>
              <dd className="text-slate-900">
                {organisation.adresse}
                {organisation.complement_adresse
                  ? `, ${organisation.complement_adresse}`
                  : ""}
                <br />
                {organisation.code_postal} {organisation.ville}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">
                <Trans>E-mail de facturation</Trans>
              </dt>
              <dd className="text-slate-900">
                {organisation.email_facturation}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    );
  }

  const creating = organisation === null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {creating ? (
            <Trans>Nouvelle organisation</Trans>
          ) : (
            <Trans>Identité et facturation</Trans>
          )}
        </CardTitle>
        <CardDescription>
          {creating ? (
            <Trans>
              Renseignez l'entité à laquelle les factures seront adressées :
              votre cabinet, votre établissement ou votre société. Vous en serez
              le gestionnaire et pourrez y inviter vos collègues.
            </Trans>
          ) : (
            <Trans>
              L'entité à laquelle les factures de l'organisation sont adressées.
              Ces informations figurent sur chaque facture.
            </Trans>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id="billing-personnel"
            checked={form.personnel}
            onCheckedChange={(checked) => update("personnel", checked === true)}
          />
          <Label htmlFor="billing-personnel" className="font-normal">
            <Trans>Facturer à titre personnel (sans SIRET)</Trans>
          </Label>
        </div>

        {!form.personnel && (
          <div className="space-y-2">
            <Label htmlFor="billing-siret">
              <Trans>SIRET</Trans>
            </Label>
            <div className="flex gap-2">
              <Input
                id="billing-siret"
                inputMode="numeric"
                value={form.siret}
                onChange={(e) => update("siret", e.target.value)}
                placeholder={t`Ex. : 123 456 789 00012`}
                className="max-w-[260px]"
              />
              <Button
                type="button"
                variant="outline"
                onClick={lookup}
                disabled={searching}
              >
                {searching ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Search className="mr-2 h-4 w-4" />
                )}
                <Trans>Préremplir</Trans>
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              <Trans>
                Le SIRET préremplit la raison sociale et l'adresse depuis le
                répertoire public des entreprises.
              </Trans>
            </p>
            {closedNotice && (
              <p className="flex items-start gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                <Info className="mt-0.5 h-4 w-4 shrink-0" />
                <Trans>
                  Cet établissement est indiqué comme fermé dans le répertoire
                  des entreprises. Vérifiez le SIRET.
                </Trans>
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {form.personnel ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="billing-prenom">
                  <Trans>Prénom</Trans>
                </Label>
                <Input
                  id="billing-prenom"
                  value={form.prenom}
                  onChange={(e) => update("prenom", e.target.value)}
                  placeholder={t`Ex. : Marie`}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="billing-nom">
                  <Trans>Nom</Trans>
                </Label>
                <Input
                  id="billing-nom"
                  value={form.nom}
                  onChange={(e) => update("nom", e.target.value)}
                  placeholder={t`Ex. : Martin`}
                />
              </div>
            </>
          ) : (
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="billing-raison-sociale">
                <Trans>Raison sociale</Trans>
              </Label>
              <Input
                id="billing-raison-sociale"
                value={form.raison_sociale}
                onChange={(e) => update("raison_sociale", e.target.value)}
                placeholder={t`Ex. : SELARL du Dr Martin`}
              />
            </div>
          )}

          {!form.personnel && (
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="billing-tva">
                <Trans>N° de TVA intracommunautaire (facultatif)</Trans>
              </Label>
              <Input
                id="billing-tva"
                value={form.numero_tva}
                onChange={(e) => update("numero_tva", e.target.value)}
                placeholder={t`Ex. : FR12345678901`}
                className="max-w-[260px]"
              />
            </div>
          )}

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="billing-adresse">
              <Trans>Adresse</Trans>
            </Label>
            <Input
              id="billing-adresse"
              value={form.adresse}
              onChange={(e) => update("adresse", e.target.value)}
              placeholder={t`Ex. : 12 rue de la République`}
            />
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="billing-complement">
              <Trans>Complément d'adresse (facultatif)</Trans>
            </Label>
            <Input
              id="billing-complement"
              value={form.complement_adresse}
              onChange={(e) => update("complement_adresse", e.target.value)}
              placeholder={t`Ex. : Bâtiment B, 2e étage`}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="billing-code-postal">
              <Trans>Code postal</Trans>
            </Label>
            <Input
              id="billing-code-postal"
              inputMode="numeric"
              maxLength={5}
              value={form.code_postal}
              onChange={(e) => update("code_postal", e.target.value)}
              placeholder={t`Ex. : 13001`}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="billing-ville">
              <Trans>Ville</Trans>
            </Label>
            <Input
              id="billing-ville"
              value={form.ville}
              onChange={(e) => update("ville", e.target.value)}
              placeholder={t`Ex. : Marseille`}
            />
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="billing-email">
              <Trans>E-mail de facturation</Trans>
            </Label>
            <Input
              id="billing-email"
              type="email"
              value={form.email_facturation}
              onChange={(e) => update("email_facturation", e.target.value)}
              placeholder={t`Ex. : comptabilite@cabinet.fr`}
            />
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        <div className="flex items-center justify-end gap-3 border-t pt-3">
          {saved && (
            <span className="flex items-center gap-1 text-sm text-primary">
              <CheckCircle2 className="h-4 w-4" />
              <Trans>Enregistré</Trans>
            </span>
          )}
          {onCancel && (
            <Button size="sm" variant="outline" onClick={onCancel}>
              <Trans>Annuler</Trans>
            </Button>
          )}
          <Button size="sm" onClick={save} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <Trans>Enregistrement…</Trans>
              </>
            ) : creating ? (
              <Trans>Créer l'organisation</Trans>
            ) : (
              <Trans>Enregistrer</Trans>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
